import runMiddleware from "lib/runMiddleware"
import { getPageData } from "services/craft/craft"
import Layout from "components/layout/layout"
import { getEmmaMeetingIdByLocale } from "services/sites"
import SessionShareMeta from "components/header/sessionShareMeta"
import { sessionPath } from "lib/sessionUrl"
import { emmaFetch } from "services/emma/server"

export default function ProgramAtAGlance({ site, page, layoutVariant, sessionTypes, user, sharedSession, shareUrl }) {
    page.showPageHeader = true
    const isLegacy = !page.singlesMatrix?.length

    return (
        <>
            <Layout
                className="atAGlance__page"
                bg="#fff"
                site={site}
                page={page}
                slug="program-at-a-glance"
                label="Program"
                matrix={page.singlesMatrix}
                user={user}
                sessionTypes={sessionTypes}
                useLegacyContent={isLegacy}
                layoutVariant={layoutVariant}
            />
            {/* Per-session Open Graph tags for session deep links, as on the program guide. Rendered
            after Layout so this title wins over the generic one from SeoMetaFields inside it. */}
            <SessionShareMeta session={sharedSession} site={site} url={shareUrl} image={null} />
        </>
    )
}

export const getServerSideProps = runMiddleware(async function (ctx) {
    const user = ctx.user

    const emmaMeetingId = getEmmaMeetingIdByLocale(ctx.locale)

    const isAlternate = ctx.params.slug?.[0] === "alt"
    const slug = isAlternate ? ctx.params.slug[1] : "program-at-a-glance"
    // A session deep link (/program-at-a-glance/<id>, or /program-at-a-glance/alt/<slug>/<id>) opens
    // that session's drawer client-side; here it only names the session for the share tags.
    const sessionId = ctx.params.slug?.[isAlternate ? 2 : 0] || null

    const requestPromises = [
        getPageData({
            locale: ctx.locale,
            page: {
                slug: slug,
                type: "programAtAGlance",
            },
            query: ctx.query,
        }),
        emmaFetch("/conferences/" + emmaMeetingId + "/categories/session-type", {
            cache: "no-store",
        }).then(response => {
            if (!response.ok) {
                console.error(`Emma API error: ${response.status} for locale ${ctx.locale}, meetingId ${emmaMeetingId}`)
                return { data: [] }
            }
            return response.json()
        }),
        // Non-fatal: a missing or pulled session falls back to the page's generic tags.
        sessionId
            ? emmaFetch("/conferences/" + emmaMeetingId + "/events/" + sessionId + "?depth=1", {
                  next: { revalidate: 1800 },
              })
                  .then(response => (response.ok ? response.json() : null))
                  .then(json => json?.event || null)
                  .catch(() => null)
            : Promise.resolve(null),
    ]

    const data = await Promise.all(requestPromises)

    // if the page is disabled, temporarily redirect to the homepage
    if (!data[0].page) {
        return {
            redirect: {
                destination: "/",
                permanent: false,
            },
        }
    }

    // Trimmed to the fields the share tags read. og:url names the session and nothing else, built
    // from the request host (see the program guide page).
    const { children: _children, ...sharedSessionFields } = data[2] || {}
    const sharedSession = data[2] ? sharedSessionFields : null
    const forwardedProto = ctx.req.headers["x-forwarded-proto"]
    const protocol = forwardedProto ? forwardedProto.split(",")[0].trim() : "http"
    const origin = ctx.req.headers.host ? protocol + "://" + ctx.req.headers.host : ""
    const shareUrl = sharedSession
        ? origin + sessionPath("/program-at-a-glance/[[...slug]]", ctx.params.slug, sessionId)
        : null

    return {
        props: {
            section: "programAtAGlance",
            sharedSession: sharedSession,
            shareUrl: shareUrl,
            site: data[0].site,
            page: data[0].page,
            sessionTypes: data[1].data ?? [],
            locale: ctx.locale,
            pageStatus: data[0].page.userStatus,
            user: user,
        },
    }
})
